<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from 'vue-i18n'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { t } = useI18n()

const loading = ref(true)
const error = ref<string | null>(null)
const processingMessage = ref('')

onMounted(async () => {
  processingMessage.value = t('auth.callback.processing.auth')
  
  try {
    // Get code and state from URL parameters
    const code = route.query.code as string
    const state = route.query.state as string
    const errorParam = route.query.error as string

    // Check for OAuth error
    if (errorParam) {
      const errorDescription = route.query.error_description as string
      throw new Error(errorDescription || `Authentication error: ${errorParam}`)
    }
    
    // Validate required parameters
    if (!code) {
      throw new Error('Missing authorization code')
    }

    if (!state) {
      throw new Error('Missing state parameter')
    }

    processingMessage.value = t('auth.callback.processing.exchanging_code')

    // Handle the authentication callback
    const result = await authStore.handleAuthCallback(code, state)

    if (result.success) {
      processingMessage.value = t('auth.callback.processing.success_redirect')
      
      // Get the registry from session storage to determine redirect
      const registry = sessionStorage.getItem('auth_registry') || 'sor'
      
      // Redirect to appropriate page based on registry
      setTimeout(() => {
        if (registry === 'sor') {
          router.push('/developer/dashboard')
        } else {
          // For other registries, could redirect to their specific pages
          router.push('/developer/dashboard')
        }
      }, 1500)
    } else {
      throw new Error(result.error || 'Authentication failed')
    }

  } catch (err) {
    const authError = err as Error
    error.value = authError.message
    console.error('Auth callback error:', authError)
    
    // Clear any partial auth state
    authStore.clearAuthentication()
    
    // Redirect to home page after delay
    setTimeout(() => {
      router.push('/')
    }, 5000)
  } finally {
    loading.value = false
  }
})

const retryAuth = () => {
  router.push('/')
}
</script>

<template>
  <div class="min-h-screen bg-gray-50 flex items-center justify-center px-4">
    <div class="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
      <!-- Loading State -->
      <div v-if="loading && !error" class="space-y-6">
        <div class="flex justify-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
        
        <div>
          <h2 class="text-xl font-semibold text-gray-900 mb-2">
            {{ $t('auth.callback.authenticating') }}
          </h2>
          <p class="text-gray-600">
            {{ processingMessage }}
          </p>
        </div>
        
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p class="text-sm text-blue-700">
            <i class="fa-solid fa-info-circle mr-2"></i>
            {{ $t('auth.callback.info_message') }}
          </p>
        </div>
      </div>

      <!-- Success State -->
      <div v-else-if="!error && !loading" class="space-y-6">
        <div class="flex justify-center">
          <i class="fa-solid fa-check-circle text-green-500 text-4xl"></i>
        </div>
        
        <div>
          <h2 class="text-xl font-semibold text-gray-900 mb-2">
            {{ $t('auth.callback.success.title') }}
          </h2>
          <p class="text-gray-600">
            {{ processingMessage }}
          </p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else class="space-y-6">
        <div class="flex justify-center">
          <i class="fa-solid fa-exclamation-triangle text-red-500 text-4xl"></i>
        </div>
        
        <div>
          <h2 class="text-xl font-semibold text-gray-900 mb-2">
            {{ $t('auth.callback.error.title') }}
          </h2>
          <p class="text-gray-600 mb-4">
            {{ $t('auth.callback.error.message') }}
          </p>
          
          <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p class="text-sm text-red-700">
              <i class="fa-solid fa-exclamation-triangle mr-2"></i>
              {{ error }}
            </p>
          </div>
        </div>
        
        <div class="space-y-3">
          <button
            @click="retryAuth"
            class="w-full bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            <i class="fa-solid fa-refresh mr-2"></i>
            {{ $t('auth.callback.error.try_again') }}
          </button>
          
          <p class="text-sm text-gray-500">
            {{ $t('auth.callback.error.redirect_message') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Loading animation for spinner */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>